<template>
	<div class="xs-12">
		<div class="block">
			<!-- Label -->
			<label :for="name" class="xs-12 fs4 fw7 lh125">
				{{ label }}
				<slot name="label"></slot>
			</label>

			<!-- Subheadline -->
			<span class="fs3 fw6 c6" v-if="subheadline">
				{{ `(${subheadline})` }}
			</span>
		</div>

		<!-- Description -->
		<div class="xs-12 fs3 lh125" v-if="description">
			{{ description }}
			<slot name="description"></slot>
		</div>

		<!-- Component for input -->
		<component
			v-if="isBeingEdited || ignoreInlineView(componentName)"
			:is="componentName"
			:id="name"
			:name="name"
			:value="value"
			:options="options"
			:placeholder="placeholder"
			:size="size"
			:status="
				validationErrors && validationErrors.first(name) ? 'error' : status
			"
			v-bind="{ ...$props, ...$attrs }"
			v-on="$listeners"
			@blur="disableIsBeingEdited()"
			ref="field"
		/>

		<!-- Value as text -->
		<div
			@click="onValueClick"
			v-if="!isBeingEdited && !ignoreInlineView(componentName)"
			class="r2 pt3 pb3 cu2 claims-value"
		>
			{{ value ? value : "---" }}
		</div>

		<!-- <div v-if="isBeingEdited" class="mt1">
			<j-button @click="isBeingEdited = false" size="xsmall">
				{{ $t("save") }}
			</j-button>
		</div> -->

		<!-- Error message -->
		<div
			class="xs-12 fs3 lh125 cError mt1"
			v-if="validationErrors && validationErrors.first(name)"
		>
			{{ validationErrors ? validationErrors.first(name) : null }}
		</div>
	</div>
</template>

<script>
/**
 * This component is a wrapper around all form inputs
 * Its main role is to standardise how we handle form inputs
 */
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputPassword from "@/components/JBaseInputPassword"
import JBaseInputLongText from "@/components/JBaseInputLongText"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import JBaseInputNumberStepper from "@/components/JBaseInputNumberStepper"
import JBaseCheckboxGroup from "@/components/JBaseCheckboxGroup"
import JBaseDatePicker from "@/components/JBaseDatePicker"
import JBaseDatePickerSimple from "@/components/JBaseDatePickerSimple"
import JBaseMultiSelect from "@/components/JBaseMultiSelect"
import JBaseMultiSelectWithFlexibleValue from "@/components/JBaseMultiSelectWithFlexibleValue"
import JBaseRadios from "@/components/JBaseRadios"
import JBaseRadiosWithDescription from "@/components/JBaseRadios"
import JBaseSelect from "@/components/JBaseSelect"
import JBaseToggle from "@/components/JBaseToggle"
import JInputList from "@/components/JInputList"
import CoreSelectCurrency from "@/modules/core/components/CoreSelectCurrency"

// List of components that will be displayed directly as it is
const ignoreInlineViewComponents = [
	"j-base-select",
	"j-base-date-picker-simple",
	"core-select-currency"
]

// List of components that will ignore autofocus once the component is in edit mode
const ignoreAutoFocusComponents = []

export default {
	name: "form-field",
	components: {
		JBaseInputText,
		JBaseInputPassword,
		JBaseInputLongText,
		JBaseInputNumber,
		JBaseInputNumberStepper,
		JBaseCheckboxGroup,
		JBaseDatePicker,
		JBaseDatePickerSimple,
		JBaseMultiSelect,
		JBaseMultiSelectWithFlexibleValue,
		JBaseRadios,
		JBaseRadiosWithDescription,
		JBaseSelect,
		JBaseToggle,
		JInputList,
		CoreSelectCurrency
	},

	props: {
		/**
		 * Important for validation and label, has to be unique in one form
		 */
		name: { type: String, required: true },
		/**
		 * It is either string e.g. j-base-input-text
		 * When it is an object, it is a component object
		 */
		componentName: {
			type: [String, Object],
			required: true
		},
		value: {
			required: true
		},
		options: {
			type: Array,
			required: false
		},
		label: {
			type: String
		},
		subheadline: { type: String },
		placeholder: { type: String },
		description: { type: String },
		/**
		 * From v-validate
		 */
		validationErrors: {
			required: false
		},
		/**
		 * Size of input component xsmall, small, normal, large, xlarge, null
		 */
		size: {
			validator(val) {
				return val === null || typeof val === "string"
			}
		},
		/**
		 * Status of input component success, error, warning, null
		 */
		status: {
			validator(val) {
				return val === null || typeof val === "string"
			}
		}
	},

	data() {
		return {
			isBeingEdited: false
		}
	},

	methods: {
		// For these components we ignore displaying inline view
		// It's e.g. select product component
		// TODO: should be computed
		ignoreInlineView(componentName) {
			if (!componentName || typeof componentName !== "string") return true // when we are passing schema of the component directly, no name
			if (ignoreInlineViewComponents.includes(componentName)) return true
			return false
		},

		onValueClick() {
			this.isBeingEdited = true

			// Once component is mounted do autofocus
			this.$nextTick(() => {
				// Check to ignore auto focus
				if (ignoreAutoFocusComponents.includes(this.componentName)) return

				// Do autofocus on possible nested elements
				this.$refs?.field?.$el?.focus()
				this.$refs?.field?.$el?.children?.[0]?.focus()
			})
		},

		disableIsBeingEdited() {
			setTimeout(() => (this.isBeingEdited = false), 0)
		}
	}
}
</script>

<style scoped>
.claims-value {
	background-color: rgba(0, 0, 0, 0);
}

.claims-value:hover {
	background-color: rgba(0, 0, 0, 0.06);
}
</style>
